import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Shell } from "@/components/layout/Shell";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { api } from "@shared/routes";
import {
  useVeiculos,
  useParceirosParaVeiculos,
  useSinistroLocalizacao,
} from "@/hooks/use-data";
import { SinistroLocalizacaoViewDialog } from "@/components/veiculos/SinistroLocalizacaoViewDialog";
import {
  VEICULO_TIPO_LABELS,
  VEICULO_TIPO_COLORS,
  MARCAS_POR_TIPO,
  CORES,
  LAUDO_STATUS_LABELS,
  LAUDO_STATUS_COLORS,
  CLASSIFICACAO_PORTE_LABELS,
} from "@/lib/constants";
import { formatPlaca } from "@/lib/formatters";
import { formatCurrency } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Car,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  FileText,
  ExternalLink,
  MapPin,
} from "lucide-react";

type VeiculoFormData = {
  tipoVeiculo: string;
  placa: string;
  chassi: string;
  cor: string;
  marca: string;
  modelo: string;
  anoFabricacao: string;
  anoModelo: string;
  renavam: string;
  classificacaoPorte: string;
  valorFipe: string;
};

type Veiculo = {
  id: string;
  placa: string | null;
  chassi: string | null;
  tipoVeiculo: string;
  marca: string | null;
  modelo: string | null;
  ano: number | null;
  anoFabricacao: number | null;
  anoModelo: number | null;
  cor: string | null;
  proprietario: string | null;
  renavam: string | null;
  numeroMotor: string | null;
  empresaId: string | null;
  parceiroId: string | null;
  criadoEm: Date | string;
  classificacaoPorte?: string | null;
  valorFipe?: string | number | null;
};

const emptyForm: VeiculoFormData = {
  tipoVeiculo: "",
  placa: "",
  chassi: "",
  cor: "",
  marca: "",
  modelo: "",
  anoFabricacao: "",
  anoModelo: "",
  renavam: "",
  classificacaoPorte: "",
  valorFipe: "",
};

export default function Veiculos() {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formData, setFormData] = useState<VeiculoFormData>(emptyForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<Veiculo | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const { session, profile } = useAuth();
  const { toast } = useToast();
  const { data: veiculos, isLoading } = useVeiculos();
  const isParceiro = profile?.empresa?.tipo === "PARCEIRO_DE_NEGOCIOS";
  const { data: parceiros } = useParceirosParaVeiculos(!isParceiro);

  const parceirosMap = (parceiros ?? []).reduce<Record<string, string>>(
    (acc, p) => {
      acc[p.id] = p.nomeFantasia || p.razaoSocial;
      return acc;
    },
    {},
  );

  const veiculosList = (veiculos as unknown as Veiculo[]) ?? [];

  const grouped = veiculosList.reduce<Record<string, Veiculo[]>>((acc, v) => {
    const key = v.parceiroId ?? "__sem_parceiro";
    if (!acc[key]) acc[key] = [];
    acc[key].push(v);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped);

  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setSearchResult(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const headers: Record<string, string> = {};
        if (session?.access_token)
          headers.Authorization = `Bearer ${session.access_token}`;
        const res = await fetch(
          `${api.veiculos.search.path}?q=${encodeURIComponent(searchQuery)}`,
          { headers },
        );
        if (res.ok) {
          const results = await res.json();
          setSearchResult(results[0] ?? null);
        }
      } finally {
        setIsSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, session?.access_token]);

  const createMutation = useMutation({
    mutationFn: async (data: VeiculoFormData) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.veiculos.create.path, {
        method: "POST",
        headers,
        body: JSON.stringify({
          ...data,
          anoFabricacao: data.anoFabricacao
            ? parseInt(data.anoFabricacao)
            : undefined,
          anoModelo: data.anoModelo ? parseInt(data.anoModelo) : undefined,
          classificacaoPorte: data.classificacaoPorte || undefined,
          valorFipe: data.valorFipe || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({
          message: "Erro ao salvar o veículo. Tente novamente.",
        }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({ title: "Veículo cadastrado com sucesso." });
      setShowForm(false);
      setFormData(emptyForm);
      setSearchResult(null);
      setSearchQuery("");
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Partial<VeiculoFormData>;
    }) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.veiculos.update.path.replace(":id", id), {
        method: "PUT",
        headers,
        body: JSON.stringify({
          ...data,
          anoFabricacao: data.anoFabricacao
            ? parseInt(data.anoFabricacao)
            : undefined,
          anoModelo: data.anoModelo ? parseInt(data.anoModelo) : undefined,
          classificacaoPorte: data.classificacaoPorte || undefined,
          valorFipe: data.valorFipe || undefined,
        }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao atualizar o veículo." }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({ title: "Veículo atualizado com sucesso." });
      setEditingId(null);
      setFormData(emptyForm);
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const headers: Record<string, string> = {};
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.veiculos.delete.path.replace(":id", id), {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Erro ao excluir veículo.");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.veiculos.list.path] });
      toast({ title: "Veículo removido com sucesso." });
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  function handleEdit(v: Veiculo) {
    setEditingId(v.id);
    setFormData({
      tipoVeiculo: v.tipoVeiculo ?? "",
      placa: v.placa ?? "",
      chassi: v.chassi ?? "",
      cor: v.cor ?? "",
      marca: v.marca ?? "",
      modelo: v.modelo ?? "",
      anoFabricacao: v.anoFabricacao ? String(v.anoFabricacao) : "",
      anoModelo: v.anoModelo ? String(v.anoModelo) : "",
      renavam: v.renavam ?? "",
      classificacaoPorte: v.classificacaoPorte ?? "",
      valorFipe: v.valorFipe != null ? String(v.valorFipe) : "",
    });
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const required = [
      "tipoVeiculo",
      "placa",
      "chassi",
      "marca",
      "modelo",
      "classificacaoPorte",
    ] as const;
    for (const field of required) {
      if (!formData[field]?.trim()) {
        toast({
          title: "Preencha todos os campos obrigatórios.",
          variant: "destructive",
        });
        return;
      }
    }
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
    setSearchResult(null);
    setSearchQuery("");
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Shell>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold font-display text-slate-900">
            Veículos
          </h1>
          <p className="text-muted-foreground mt-1">
            Cadastre e gerencie os veículos da sua empresa
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Cadastrar Veículo
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="mb-4">
              <Label htmlFor="search-input">
                Buscar por placa ou chassi (verificar duplicata)
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="search-input"
                  placeholder="Digite placa ou chassi para verificar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {isSearching && (
                  <Loader2 className="w-4 h-4 animate-spin self-center text-slate-400" />
                )}
              </div>
            </div>

            {searchResult && (
              <div className="mb-4 p-4 border border-amber-200 bg-amber-50 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">
                    Veículo já cadastrado
                  </p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    {searchResult.placa
                      ? formatPlaca(searchResult.placa)
                      : searchResult.chassi}{" "}
                    — {searchResult.marca} {searchResult.modelo}{" "}
                    {searchResult.anoFabricacao}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    handleEdit(searchResult);
                    setSearchResult(null);
                    setSearchQuery("");
                  }}
                >
                  Usar este veículo
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="tipo">
                    Tipo de Veículo <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.tipoVeiculo}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, tipoVeiculo: v, marca: "" }))
                    }
                  >
                    <SelectTrigger id="tipo" className="mt-1">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(VEICULO_TIPO_LABELS).map(([k, label]) => (
                        <SelectItem key={k} value={k}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="placa">
                    Placa <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="placa"
                    className="mt-1"
                    value={formData.placa}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, placa: e.target.value }))
                    }
                    placeholder="ABC-1234"
                  />
                </div>

                <div>
                  <Label htmlFor="chassi">
                    Chassi <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="chassi"
                    className="mt-1"
                    value={formData.chassi}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, chassi: e.target.value }))
                    }
                    placeholder="17 caracteres"
                  />
                </div>

                <div>
                  <Label htmlFor="marca">
                    Marca <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.marca}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, marca: v }))
                    }
                    disabled={!formData.tipoVeiculo}
                  >
                    <SelectTrigger id="marca" className="mt-1">
                      <SelectValue
                        placeholder={
                          formData.tipoVeiculo
                            ? "Selecione a marca"
                            : "Selecione o tipo primeiro"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(MARCAS_POR_TIPO[formData.tipoVeiculo] ?? []).map(
                        (m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="modelo">
                    Modelo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="modelo"
                    className="mt-1"
                    value={formData.modelo}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, modelo: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="anoFabricacao">Ano Fabricação</Label>
                  <Input
                    id="anoFabricacao"
                    className="mt-1"
                    type="number"
                    min={1900}
                    max={new Date().getFullYear() + 1}
                    value={formData.anoFabricacao}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        anoFabricacao: e.target.value,
                      }))
                    }
                    placeholder="2020"
                  />
                </div>

                <div>
                  <Label htmlFor="anoModelo">Ano Modelo</Label>
                  <Input
                    id="anoModelo"
                    className="mt-1"
                    type="number"
                    min={1900}
                    max={new Date().getFullYear() + 2}
                    value={formData.anoModelo}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        anoModelo: e.target.value,
                      }))
                    }
                    placeholder="2020"
                  />
                </div>

                <div>
                  <Label htmlFor="cor">Cor</Label>
                  <Select
                    value={formData.cor}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, cor: v }))
                    }
                  >
                    <SelectTrigger id="cor" className="mt-1">
                      <SelectValue placeholder="Selecione a cor" />
                    </SelectTrigger>
                    <SelectContent>
                      {CORES.map((c) => (
                        <SelectItem
                          key={c.codigo}
                          value={`${c.codigo} - ${c.nome}`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0"
                              style={{
                                background:
                                  c.hex ??
                                  "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)",
                              }}
                            />
                            {c.codigo} - {c.nome}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="renavam">RENAVAM</Label>
                  <Input
                    id="renavam"
                    className="mt-1"
                    value={formData.renavam}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, renavam: e.target.value }))
                    }
                    placeholder="00000000000"
                  />
                </div>

                <div>
                  <Label htmlFor="classificacaoPorte">
                    Classificação de Porte{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.classificacaoPorte}
                    onValueChange={(v) =>
                      setFormData((p) => ({ ...p, classificacaoPorte: v }))
                    }
                  >
                    <SelectTrigger id="classificacaoPorte" className="mt-1">
                      <SelectValue placeholder="Selecione a classificação" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CLASSIFICACAO_PORTE_LABELS).map(
                        ([k, label]) => (
                          <SelectItem key={k} value={k}>
                            {label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="valorFipe">Valor FIPE</Label>
                  <CurrencyInput
                    id="valorFipe"
                    className="mt-1"
                    value={formData.valorFipe}
                    onChange={(v) =>
                      setFormData((p) => ({ ...p, valorFipe: v }))
                    }
                    placeholder="0,00"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isPending}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {editingId ? "Salvar Alterações" : "Cadastrar"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array(3)
            .fill(null)
            .map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6 space-y-2">
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/4" />
                </CardContent>
              </Card>
            ))}
        </div>
      ) : !veiculos || veiculos.length === 0 ? (
        <div className="text-center py-16">
          <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700">
            Nenhum veículo cadastrado
          </h3>
          <p className="text-slate-500 mt-1 text-sm">
            Cadastre o primeiro veículo da sua empresa para iniciar uma
            reclassificação de monta.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupKeys.map((key) => {
            const group = grouped[key];
            const empresaNome =
              key === "__sem_parceiro"
                ? "Sem empresa"
                : (parceirosMap[key] ?? "Empresa desconhecida");
            return (
              <div key={key}>
                {!isParceiro && (
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 px-1">
                    {empresaNome}
                    <span className="ml-2 text-xs font-normal normal-case">
                      ({group.length}{" "}
                      {group.length === 1 ? "veículo" : "veículos"})
                    </span>
                  </h2>
                )}
                <div className="space-y-3">
                  {group.map((v) => (
                    <VeiculoCard
                      key={v.id}
                      veiculo={v}
                      expanded={expandedId === v.id}
                      onToggleExpand={() =>
                        setExpandedId(expandedId === v.id ? null : v.id)
                      }
                      onEdit={() => handleEdit(v)}
                      onDelete={() => deleteMutation.mutate(v.id)}
                      session={session}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}

function VeiculoCard({
  veiculo,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  session,
}: {
  veiculo: Veiculo;
  expanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  session: { access_token?: string } | null;
}) {
  const [, navigate] = useLocation();
  const [showLocalizacao, setShowLocalizacao] = useState(false);

  const { data: laudos, isLoading: laudosLoading } = useQuery({
    queryKey: [api.remontaLaudos.list.path, veiculo.id],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(
        `${api.remontaLaudos.list.path}?veiculoId=${veiculo.id}`,
        { headers },
      );
      if (!res.ok) throw new Error("Erro ao carregar laudos");
      return res.json() as Promise<
        {
          id: string;
          codigo: string | null;
          status: string;
          criadoEm: string;
        }[]
      >;
    },
    enabled: expanded,
  });
  const { data: sinistroLocalizacao } = useSinistroLocalizacao(
    veiculo.id,
    expanded,
  );

  const tipoColor =
    VEICULO_TIPO_COLORS[veiculo.tipoVeiculo] ?? "bg-slate-100 text-slate-700";
  const tipoLabel =
    VEICULO_TIPO_LABELS[veiculo.tipoVeiculo] ?? veiculo.tipoVeiculo;

  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">
                {veiculo.placa ? formatPlaca(veiculo.placa) : "Sem Placa"}
              </span>
              <Badge className={tipoColor}>{tipoLabel}</Badge>
            </div>
            <p className="text-sm text-slate-600 mt-0.5">
              {[
                veiculo.marca,
                veiculo.modelo,
                veiculo.anoFabricacao || veiculo.anoModelo,
              ]
                .filter(Boolean)
                .join(" ")}
              {veiculo.cor && ` · ${veiculo.cor}`}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Classificação:{" "}
              {veiculo.classificacaoPorte
                ? CLASSIFICACAO_PORTE_LABELS[veiculo.classificacaoPorte]
                : "Não informado"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              FIPE:{" "}
              {veiculo.valorFipe != null
                ? formatCurrency(Number(veiculo.valorFipe))
                : "Não informado"}
            </p>
          </div>

          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleExpand}
              aria-expanded={expanded}
              className="gap-1 text-xs"
            >
              {expanded ? (
                <ChevronUp className="w-3.5 h-3.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5" />
              )}
              Ver Laudos
            </Button>
            {sinistroLocalizacao && (
              <Button
                size="sm"
                variant="ghost"
                className="gap-1 text-xs"
                onClick={() => setShowLocalizacao(true)}
              >
                <MapPin className="w-3.5 h-3.5" />
                Ver Localização do Sinistro
              </Button>
            )}
            <Button size="icon" variant="ghost" onClick={onEdit} title="Editar">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="text-red-500 hover:text-red-700"
                  title="Excluir"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir veículo?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação removerá permanentemente o veículo{" "}
                    {veiculo.placa
                      ? formatPlaca(veiculo.placa)
                      : veiculo.chassi}
                    . Laudos vinculados não serão afetados.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Excluir
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 border-t pt-4">
            {laudosLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-3/4" />
              </div>
            ) : !laudos || laudos.length === 0 ? (
              <p className="text-sm text-slate-500">
                Nenhum laudo associado a este veículo.
              </p>
            ) : (
              <ul className="space-y-1">
                {laudos.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="font-mono text-xs text-slate-700">
                        {l.codigo ?? `#${l.id.slice(0, 8)}`}
                      </span>
                      {l.criadoEm && (
                        <span className="text-xs text-slate-400 hidden sm:inline">
                          {new Date(l.criadoEm).toLocaleDateString("pt-BR")}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        className={
                          LAUDO_STATUS_COLORS[l.status] ??
                          "bg-slate-100 text-slate-700"
                        }
                      >
                        {LAUDO_STATUS_LABELS[l.status] ?? l.status}
                      </Badge>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
                        onClick={() => navigate(`/laudos/${l.id}`)}
                        title="Abrir laudo"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
      {sinistroLocalizacao && (
        <SinistroLocalizacaoViewDialog
          open={showLocalizacao}
          onOpenChange={setShowLocalizacao}
          localizacao={sinistroLocalizacao}
        />
      )}
    </Card>
  );
}
